import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { Unterzeichner } from '../shared/constants';
import { formatDDMMYYYY } from '../shared/dateTime';
import { Vorgang } from '../types';
import { Signature } from './common/Signature';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import { MEHRWERTSTEUER } from '../shared/domain/vorgang';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function AuftragZurErstellungReparaturbestaetigung({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.auftragZurErstellungReparaturnachweis}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ auftragZurErstellungReparaturnachweis: event.target.checked })}
              name="auftragZurErstellungReparaturnachweis"
              color="primary"
              data-testid="auftragZurErstellungReparaturnachweisCheckbox"
            />
          }
          label={
            <>
              Ich habe die AGB{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="auftragZurErstellungReparaturbestaetigungLink"
              >
                Auftrag zur Erstellung einer Reparaturbestätigung nach Instandsetzung eines Fahrzeugs
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.*
            </>
          }
        />
      </Grid>
      {isModalOpen && <AuftragModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function AuftragModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const url = useDateiUrl(vorgang?.unterschriften?.auftragZurErstellungReparaturnachweis?.key);

  const signDate = vorgang?.unterschriften?.auftragZurErstellungReparaturnachweis?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Auftrag zur Erstellung einer Reparaturbestätigung nach Instandsetzung eines Fahrzeugs"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="auftragModal">
          <Grid size={12}>
            <Grid container>
              <Grid size={4}>
                Zum Reparaturnachweis
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="vorgangsnummer">
                {vorgang.vorgangsnummer}
              </Grid>
              <Grid size={4}>
                Schadensereignis vom
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="unfalldatum">
                {formatDDMMYYYY(vorgang?.unfalldaten?.datum)}
              </Grid>
              <Grid size={4}>
                Name des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="nameAuftraggeber">
                {vorgang.auftraggeber?.anrede} {vorgang.auftraggeber?.vorname} {vorgang.auftraggeber?.nachname}
              </Grid>
              <Grid size={4}>
                Anschrift des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="anschriftAuftraggeber">
                {vorgang?.auftraggeber?.strasse} {vorgang?.auftraggeber?.plz} {vorgang?.auftraggeber?.ort}
              </Grid>
              <Grid size={4}>
                Kennzeichen des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="kennzeichenAuftraggeber">
                {vorgang.fahrzeugdaten.kennzeichen}
              </Grid>
              <Grid size={4}>
                Kennzeichen des Unfallgegners
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="kennzeichenUnfallgegener">
                {vorgang?.unfalldaten?.kennzeichen}
              </Grid>
              <Grid size={4}>
                Name des Auftragnehmers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="nameAuftragnehmer">
                Experts24 Tassone GmbH, Hildener Str. 28, 42697 Solingen
              </Grid>
              <Grid size={4}>
                Fahrzeug steht im Betriebsvermögen
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="betriebsvermoegen">
                {vorgang?.auftraggeberVorsteuerabzugsberechtigt ? 'Ja' : 'Nein'}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§1 Geltung der Bedingungen für die Reparaturbestätigung</Typography>
            <Typography variant="body2">
              Diese Allgemeinen Geschäftsbedingungen gelten für die Erstellung einer Reparaturbestätigung durch einen Kfz-Sachverständigen der Experts24 Tassone
              GmbH.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§2 Auftragserteilung</Typography>
            <Typography variant="body2">
              Der Auftrag zur Erstellung einer Reparaturbestätigung erfolgt regelmäßig durch (auch digitale) Unterschrift des AG unter dem Auftragsformular und
              Zugang des unterschriebenen Auftragsformulars beim AN.
            </Typography>
            <Typography variant="body2">
              Der Auftrag kann alternativ auch im Wege der elektronischen Auftragserteilung zustande kommen. In diesem Fall gibt der AG ein verbindliches
              Angebot ab, indem er
            </Typography>
            <Typography variant="body2">
              - die im Beauftragungsprozess bereitgestellten Vertragsbedingungen durch Anklicken der hierfür vorgesehenen Check-Boxen akzeptiert und
            </Typography>
            <Typography variant="body2">
              - den Auftrag durch Betätigung des eindeutig beschrifteten Buttons „kostenpflichtig bestellen“ bzw. „kostenpflichtig beauftragen“ absendet.
            </Typography>
            <Typography variant="body2">
              Die Auftragsannahme erfolgt durch Erklärung des AN gegenüber dem AG, insbesondere durch elektronische Bestätigung der Auftragsannahme.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§3 Leistungsumfang</Typography>
            <Typography variant="body2">
              (1) Der Sachverständige dokumentiert im Rahmen der Reparaturbestätigung den Zustand des Fahrzeugs zum Zeitpunkt der Besichtigung. <br />
              (2) Die Reparaturbestätigung umfasst insbesondere: <br />
              <ul>
                <li>die Sichtprüfung der ausgeführten Reparaturmaßnahmen,</li>
                <li>den Abgleich mit einem ggf. zuvor erstellten Schadengutachten,</li>
                <li>die Dokumentation noch vorhandener, erkennbarer Vorschäden,</li>
                <li>die Beurteilung, ob die Reparatur sach- und fachgerecht durchgeführt wurde,</li>
                <li>soweit möglich, eine Beschreibung des eingeschlagenen Reparaturwegs,</li>
                <li>eine zusammenfassende Aussage zum Reparaturerfolg,</li>
                <li>die Durchführung einer Lackschichtendickenmessung.</li>
              </ul>
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§4 Vergütung des Sachverständigen</Typography>
            <Typography variant="body2">Die Vergütung des Sachverständigen berechnet sich wie folgt:</Typography>
            <Typography variant="body2">
              <table width="50%">
                <thead>
                  <tr>
                    <th align="left">Position</th>
                    <th align="right">Netto</th>
                    <th align="right">MwSt ({MEHRWERTSTEUER}%)</th>
                    <th align="right">Brutto</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td align="left">Grundhonorar</td>
                    <td align="right">25,21 €</td>
                    <td align="right">4,79 €</td>
                    <td align="right">30,00 €</td>
                  </tr>
                  <tr>
                    <td align="left">1. Fotosatz je Bild</td>
                    <td align="right">1,68 €</td>
                    <td align="right">0,32 €</td>
                    <td align="right">2,00 €</td>
                  </tr>
                  <tr>
                    <td align="left">2. Fotosatz je Bild</td>
                    <td align="right">0,42 €</td>
                    <td align="right">0,08 €</td>
                    <td align="right">0,50 €</td>
                  </tr>
                  <tr>
                    <td align="left">Schreibkosten / Kopien</td>
                    <td align="right">14,12 €</td>
                    <td align="right">2,68 €</td>
                    <td align="right">16,80 €</td>
                  </tr>
                  <tr>
                    <td align="left">Lackschichtenmessung</td>
                    <td align="right">70,00 €</td>
                    <td align="right">13,30 €</td>
                    <td align="right">83,30 €</td>
                  </tr>
                </tbody>
              </table>
            </Typography>
            <Typography variant="body2">Alle Beträge verstehen sich inkl. der gesetzlichen Mehrwertsteuer in Höhe von derzeit {MEHRWERTSTEUER} %</Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={url}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.auftragZurErstellungReparaturnachweis?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
